/**
 * P1.8: Shared authorization fingerprint for consistent cache keys across all
 * authorization-dependent queries.
 *
 * All queries that depend on user permissions, scope, or persona should include
 * this fingerprint in their queryKey to ensure cache invalidation when any
 * authorization dimension changes.
 */
import type { User, CurrentContext } from '../../features/auth/auth.types.js';

interface AuthorizationContext extends CurrentContext {
  clientAccountId?: string;
  portfolioId?: string;
  supportDelegationId?: string;
}

export interface AuthorizationFingerprint {
  /** User identity ID */
  userId?: string;
  /** Current organization scope */
  organizationId?: string;
  /** Current branch scope */
  branchId?: string;
  /** Current client scope */
  clientAccountId?: string;
  /** Current portfolio scope */
  portfolioId?: string;
  /** Support delegation scope */
  supportDelegationId?: string;
  /** User's current persona */
  persona?: string;
  /** Authentication assurance level */
  aal?: string;
  /** Incremented when entitlements change */
  entitlementsVersion?: number;
}

export function buildAuthorizationFingerprint(
  user: User | null,
  currentContext: AuthorizationContext | null,
): AuthorizationFingerprint {
  return {
    userId: user?.id,
    organizationId: currentContext?.organizationId,
    branchId: currentContext?.branchId,
    clientAccountId: currentContext?.clientAccountId,
    portfolioId: currentContext?.portfolioId,
    supportDelegationId: currentContext?.supportDelegationId,
    persona: user?.persona,
    aal: user?.aal,
    entitlementsVersion: user?.entitlementsVersion,
  };
}

export function fingerprintToArray(fingerprint: AuthorizationFingerprint): readonly (string | number | undefined)[] {
  return [
    fingerprint.userId,
    fingerprint.organizationId,
    fingerprint.branchId,
    fingerprint.clientAccountId,
    fingerprint.portfolioId,
    fingerprint.supportDelegationId,
    fingerprint.persona,
    fingerprint.aal,
    fingerprint.entitlementsVersion,
  ] as const;
}
